import { PlayerState } from './gameState';

export interface Pairing {
  playerAId: string;
  playerBId: string;
}

/**
 * Shuffles an array in place using the Fisher-Yates algorithm.
 * @param array The array to shuffle.
 */
function shuffleArray<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]]; // Swap elements
  }
  return array;
}

/**
 * Generates a list of random pairings for all alive players for a combat round.
 * @param players - The array of all players in the game.
 * @param round - The current round number (for potential future seeding).
 * @returns An array of Pairing objects.
 */
export function generatePairings(players: PlayerState[], round: number): Pairing[] {
  const alivePlayers = players.filter(p => p.alive);
  const shuffledPlayers = shuffleArray([...alivePlayers]);
  const pairings: Pairing[] = [];

  while (shuffledPlayers.length > 1) {
    const playerA = shuffledPlayers.shift()!;
    const playerB = shuffledPlayers.shift()!;
    pairings.push({ playerAId: playerA.id, playerBId: playerB.id });
  }

  // Handle the case of an odd number of players by creating a "ghost" fight.
  // The player fights a copy of themselves, which is treated as a win.
  if (shuffledPlayers.length === 1) {
    const lastPlayer = shuffledPlayers.shift()!;
    pairings.push({ playerAId: lastPlayer.id, playerBId: lastPlayer.id });
  }

  return pairings;
}
